import {
  Select,
  Switch,
  Typography,
  Flex,
  Form,
  Button,
  Badge,
  message,
  Spin,
} from "antd";
import {
  PlusOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
  ContactsOutlined,
} from "@ant-design/icons";
import { useState, useMemo } from "react";
import { axiosInstance, displayEmail, generateRandomPassword } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import { debounce } from "@/utilities/debounce";
import { t } from "i18next";
import { TwoPaneCascaderSearch } from "../cascader";
import { CascaderProps } from "antd/lib/cascader";
import { DefaultOptionType } from "antd/es/select";
import { SellerIcon } from "../icon";
import { ShopOutlined } from "@ant-design/icons";
import { ContactResponse } from "@/types/schema/xconsTypess";
import { NewContactCard } from "../smart-select-contacts/forms/new-contact";
import { useContactConflictCheck } from "@/hooks/useContactConflictCheck";
import { NIVEL } from "@/utilities/niveles-de-permiso";

const { Option } = Select;
const { Text } = Typography;

type ApiContact = {
  contactId: number;
  userId: number;
  storeCode?: string;
  isActive?: number | boolean;
  permissions?: Array<{ store?: string; seller?: string }>;
  storeOwner?: string | null;
  sellerCode?: string | null;
  user?: {
    userId: number;
    email: string;
    firstname: string;
    lastname: string;
    whatsapp?: string | null;
  };
};

type ContactOption = {
  id: number;
  contactId: number;
  name: string;
  email: string;
  whatsapp: string;
  type: string;
  role?: string;
  roleCode?: string;
};

type Contact = {
  userId: number;
  email: string;
  firstname: string;
  lastname: string;
  contactId: number;
  storeCode: string;
  isActive: boolean;
  whatsapp: string;
};

type UserWithContacts = {
  userId: number;
  contactId: number;
  email: string;
  firstname: string;
  lastname: string;
  contacts: Contact[];
  permissions: Array<{ store?: string; seller?: string }>;
  user: {
    userId: number;
    email: string;
    firstname: string;
    lastname: string;
    whatsapp?: string | null;
  };
};

type CascaderContactOption = DefaultOptionType & {
  _labelText?: string;
  _searchText?: string;
  _item?: ContactOption;
  _meta?: any;
  children?: CascaderContactOption[];
};

const selectOptions = [
  { value: "admin", label: NIVEL.admin },
  { value: "editor", label: NIVEL.editor },
  { value: "viewer", label: NIVEL.viewer },
  { value: "no_access", label: "No Access" },
];

interface PermissionDescriptor {
  code: string;
  label: string;
  badge?: boolean;
  /**
   * Se renderiza como switch en vez de select. Payment y Delivery son sub-permisos de acceso
   * on/off, no niveles: el mismo criterio que ya usa el panel de ver participantes
   * (`PermissionSelectRow asSwitch`) y el de configuración del operador.
   */
  asSwitch?: boolean;
}

/** ON del switch. `editor` y no `admin`: da acceso de trabajo, no de administración. */
const SWITCH_ON = "editor";
/** OFF del switch. Se manda explícito: sin valor la participación queda sin nivel. */
const SWITCH_OFF = "no_access";

const DEFAULT_PERMISSIONS: PermissionDescriptor[] = [
  { code: "order", label: "Order" },
  { code: "order_payment", label: "Pago", badge: true, asSwitch: true },
  { code: "order_shipment", label: "Entregas", badge: true, asSwitch: true },
];

interface PermissionsSelectProps {
  handleCancel: () => void;
  contactStores: ContactResponse;
  sectionType?: "contact" | "store";
  contactId?: number;
  onSaveContact: (
    contact: ContactOption,
    permissions: { [code: string]: string },
  ) => Promise<void>;
  store: { code: string; name: string; link: string } | undefined;
  /**
   * Permissions rendered in the form. Defaults to Order / Payment / Delivery
   * (legacy Pedidos shape). Pass a single-item array to limit the popup to
   * one permission (e.g. `[{ code: "quote", label: "Presupuesto" }]`).
   */
  permissions?: PermissionDescriptor[];
  /**
   * Contact IDs already participating in the parent entity (quote, order,
   * etc.). These get hidden from the cascader results so the operator can't
   * add the same participant twice.
   */
  excludedContactIds?: number[];
}

export const PermissionsSelect = ({
  handleCancel,
  onSaveContact,
  store,
  contactStores,
  sectionType = "contact",
  contactId,
  permissions: permissionsProp,
  excludedContactIds,
}: PermissionsSelectProps) => {
  const permissionList = permissionsProp ?? DEFAULT_PERMISSIONS;
  const primaryPermission = permissionList[0];
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [contacts, setContacts] = useState<ApiContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactOption | null>(null);

  const [selectedValues, setSelectedValues] = useState<Record<string, string>>({});
  const setPermissionValue = (code: string, value: any) =>
    setSelectedValues((prev) => ({ ...prev, [code]: value }));
  const hasAnySelectedValue = Object.values(selectedValues).some(Boolean);

  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [cascaderOpen, setCascaderOpen] = useState(false);

  const addedParticipationsSet = useMemo(() => new Set<string>(), []);

  const [form] = Form.useForm();

  const {
    runConflictCheck,
    applyConflictErrors,
    consumeBackendConflict,
  } = useContactConflictCheck(form);

  // Same on-blur validation pattern used in `SmartSelectContact` /
  // `contactsCreate`: hit /users/lookup, apply the matrix, pin errors to the
  // offending Form.Item.
  const handleNewContactBlurValidate = async () => {
    const whatsapp = form.getFieldValue("whatsapp");
    const email = form.getFieldValue("email");
    const { whatsappError, emailError } = await runConflictCheck(
      whatsapp,
      email,
      store?.code,
    );
    applyConflictErrors(whatsappError, emailError);
  };

  const handleAddContact = () => {
    setIsFormOpen(true);
    setIsSelected(false);
    setSelectedContact(null);
    form.resetFields();
  };

  const resetFormState = () => {
    setSelectedContact(null);
    setSelectedValues({});
    setIsFormOpen(false);
    setIsSelected(false);
    form.resetFields();
    setLoadingSave(false);
  };

  const normalizePermissionCodes = (permissions: any): Set<string> => {
    const set = new Set<string>();

    if (Array.isArray(permissions)) {
      for (const p of permissions) {
        const storeCode = String(p?.store ?? "").trim();
        const sellerCode = String(p?.seller ?? "").trim();
        if (storeCode) set.add(`store:${storeCode}`);
        if (sellerCode) set.add(`seller:${sellerCode}`);
      }
      return set;
    }

    const stores = permissions?.store;
    const sellers = permissions?.seller;

    if (Array.isArray(stores)) {
      for (const s of stores) {
        const code = String(s?.code ?? s ?? "").trim();
        if (code) set.add(`store:${code}`);
      }
    }
    if (Array.isArray(sellers)) {
      for (const s of sellers) {
        const code = String(s?.code ?? s ?? "").trim();
        if (code) set.add(`seller:${code}`);
      }
    }

    return set;
  };

  /**
   * Acota los permisos del contacto al store en el que se está agregando la relación.
   *
   * **Sólo los de `store`.** Los de `seller` se dejan pasar tal cual: el código de un seller no
   * tiene por qué parecerse al del store que lo tiene vinculado, así que compararlos entre sí
   * descarta al operador de todo seller que no se llame igual que el store. Eso dejaba sin su rol
   * a los 23 operadores de seller con ficha en `vv` y a los 12 de `mix_pinturerias`, mientras que
   * en los stores de un solo seller (`comercialcmp`, `mocona`, `puntoseco`…) no se notaba porque
   * ahí `seller_code == store_code`. El operador terminaba ofrecido como "Customer" por el
   * fallback de `getBaseRoleFromContact`.
   *
   * El scope del seller ya viene resuelto del backend, que es el único lado que puede hacerlo:
   * `GET /contacts/search` sólo emite `{"seller": X}` si X está en `store_linked_sellers` del
   * store pedido y activo. Volver a filtrar acá era redundante además de incorrecto — y es lo que
   * `SmartSelectContact` (el buscador de Contactos) nunca hizo, de ahí que ahí sí funcionara.
   */
  const filterPermissionsByStoreCode = (permissions: any, storeCode: string) => {
    if (!storeCode) return permissions;

    if (Array.isArray(permissions)) {
      return permissions.filter((p) => {
        const storeVal = String(p?.store ?? "").trim();
        // Los de `store` se acotan al store actual; los de `seller` pasan enteros. Una entrada sin
        // ninguno de los dos no la emite el endpoint, y se descarta como se descartaba antes.
        if (storeVal) return storeVal === storeCode;
        return !!String(p?.seller ?? "").trim();
      });
    }

    if (permissions && typeof permissions === "object") {
      const stores = Array.isArray(permissions?.store) ? permissions.store : [];

      const filteredStores = stores.filter((s: any) => String(s?.code ?? s ?? "").trim() === storeCode);

      return {
        ...permissions,
        store: filteredStores,
      };
    }

    return permissions;
  };

  const getContacts = async (searchValue: string = "") => {
    if (loadingContacts) return;
    setLoadingContacts(true);
    const sCode = String(contactStores?.store?.code ?? "").trim();

    try {
      const { data } = await axiosInstance.get(`${API_BASE_URL}/contacts/search`, {
        params: { q: searchValue?.trim(), limit: 60, storeCode: sCode },
      });

      const incomingContacts: ContactResponse[] = Array.isArray(data) ? data : [];

      const filteredContacts =
        sectionType === "contact"
          ? incomingContacts.filter((c) => {
            if (c?.contactId === contactId) return false;

            if (!sCode) return true;

            const permsSet = normalizePermissionCodes((c as any)?.permissions);

            const hasStore = permsSet.has(`store:${sCode}`);
            const hasSeller = permsSet.has(`seller:${sCode}`);

            if (hasStore || hasSeller) return true;

            return String((c as any)?.storeCode ?? "").trim() === sCode;
          })
          : incomingContacts;

      const finalContacts =
        sectionType === "contact" && sCode
          ? filteredContacts.map((c) => ({
            ...c,
            permissions: filterPermissionsByStoreCode((c as any)?.permissions, sCode),
          }))
          : filteredContacts;

      setContacts(finalContacts);
    } catch (error) {
      console.error("Error al cargar contactos:", error);
    } finally {
      setLoadingContacts(false);
    }
  };


  const debouncedSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        setSearchTerm(value);
        await getContacts(value);
      }, 500),
    [],
  );

  const handleSearch = (value: string) => {
    setInputValue(value);
    debouncedSearch(value);
  };

  const roleRow = (storeLabel: string, roleText: string) => {
    const roleLower = roleText.toLowerCase();
    let Icon = (roleLower.includes("seller")) ? SellerIcon :
      (roleLower.includes("customer")) ? ContactsOutlined : ShopOutlined;

    return (
      <Flex align="center" gap={8}>
        <Icon style={{ fontSize: 14 }} />
        <Text style={{ textTransform: "capitalize" }}>{roleText}</Text>
      </Flex>
    );
  };

  const getBaseRoleFromContact = (c: ApiContact): {
    role: "store" | "seller" | "customer";
    code: string;
  } => {
    const storeOwner = String(c?.storeOwner ?? "").trim();
    const sellerCode = String(c?.sellerCode ?? "").trim();

    if (storeOwner) return { role: "store", code: storeOwner };
    if (sellerCode) return { role: "seller", code: sellerCode };
    return { role: "customer", code: "" };
  };

  const cascaderOptions = useMemo(() => {
    // ✅ dedupe por contactId (más seguro que userId para este endpoint)
    const excludedSet = new Set(
      (excludedContactIds ?? []).map((id) => Number(id)).filter(Boolean),
    );
    const unique = Array.from(
      new Map(contacts.map((c) => [c.contactId, c])).values(),
    ).filter((c) => !excludedSet.has(Number(c.contactId)));

    return unique.map((c) => {
      const u = c.user;

      const item: ContactOption = {
        id: Number(u?.userId ?? c.userId),          // userId real
        // `null` cuando la persona es operadora del store (o de un seller asociado) pero todavía
        // no tiene ficha acá. `Number(null)` daría 0, que el backend leería como un contactId
        // válido: se preserva el null y se manda `userId` en su lugar.
        contactId: c.contactId != null ? Number(c.contactId) : (null as any),
        name: `${u?.firstname ?? ""} ${u?.lastname ?? ""}`.trim() || "-",
        email: u?.email ?? "-",
        whatsapp: String(u?.whatsapp ?? ""),
        type: "contact",
      };

      // ✅ roles dinámicos según permissions del contacto
      const permissionEntries = Array.isArray(c.permissions) ? c.permissions : [];
      const stores = Array.from(
        new Set(permissionEntries.map((p) => p.store).filter(Boolean)),
      ) as string[];

      const sellers = Array.from(
        new Set(permissionEntries.map((p) => p.seller).filter(Boolean)),
      ) as string[];

      const children: CascaderContactOption[] = [];

      /**
       * El rol que se ofrece es el que la persona REALMENTE tiene en este store.
       *
       * El backend ya no fabrica cargos: hasta el 2026-08-25, si alguien tenía cargos en otro
       * store pero ninguno acá, `GET /contacts/search` devolvía `[{store: <este store>}]` y esto
       * lo pintaba como "Store Operator". Un cliente de Puntoseco que era operador de Tecnopor se
       * ofrecía —y se guardaba— como Store Operator de Puntoseco. Eran 183 contactos en esa
       * condición.
       *
       * Ahora un array vacío significa lo que dice: es un cliente del store.
       */
      // store perms
      for (const storeCode of stores) {
        children.push({
          value: `store:${storeCode}`,
          label: roleRow(storeCode, t("contacts.role.store_operator")),
          _meta: { role: "store", code: storeCode },
          _item: item,
        });
      }

      // seller perms — el seller se NOMBRA: "a qué seller pertenece" es parte del requerimiento,
      // y sin eso dos operadores de sellers distintos se ven idénticos en la lista.
      for (const sellerCode of sellers) {
        children.push({
          value: `seller:${sellerCode}`,
          label: roleRow(sellerCode, `${t("contacts.role.seller_operator")} · ${sellerCode}`),
          _meta: { role: "seller", code: sellerCode },
          _item: item,
        });
      }

      // fallback: si no tiene perms, al menos dejale elegir el store actual
      if (children.length === 0) {
        const base = getBaseRoleFromContact(c);
        const addedKey = `${c.contactId}:${base.role}:${base.code ?? ""}`;

        if (!addedParticipationsSet.has(addedKey)) {
          if (base.role === "store") {
            children.push({
              value: `store:${base.code}`,
              label: roleRow(base.code || "Store", t("contacts.role.store_operator")),
              _meta: { role: "store", code: base.code },
              _item: item,
            });
          } else if (base.role === "seller") {
            children.push({
              value: `seller:${base.code}`,
              label: roleRow(base.code || "Seller", t("contacts.role.seller_operator")),
              _meta: { role: "seller", code: base.code },
              _item: item,
            });
          } else {
            children.push({
              value: `customer`,
              label: roleRow("Customer", t("contacts.role.customer")),
              _meta: { role: "customer", code: "" },
              _item: item,
            });
          }
        }
      }

      return {
        value: item.id,
        label: (
          <Flex vertical gap={2}>
            <Text strong>{item.name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {displayEmail(item.email)}
            </Text>
          </Flex>
        ),
        _labelText: item.name,
        _searchText: `${item.name} ${item.email} ${item.whatsapp}`.toLowerCase(),
        _item: item,
        children,
      };
    });
  }, [contacts, store?.code, excludedContactIds]);

  const onCascaderChange: CascaderProps["onChange"] = (_path, selected) => {
    const top = selected?.[0] as CascaderContactOption;
    const last = selected?.[selected.length - 1] as CascaderContactOption;

    if (!top?._item) return;

    const contact = top._item;
    setSelectedContact({
      ...contact,
      role: last?._meta?.role,
      roleCode: last?._meta?.code,
    });
    setIsSelected(true);
    setIsFormOpen(false);

    form.setFieldsValue({
      firstname: contact.name.split(" ")[0],
      lastname: contact.name.split(" ").slice(1).join(" "),
      email: contact.email,
      whatsapp: contact.whatsapp,
    });
  };

  const getRoleIcon = (role?: string) => {
    switch (role) {
      case "store":   return <ShopOutlined />;
      case "seller":  return <SellerIcon />;
      case "customer":
      default:        return <ContactsOutlined />;
    }
  };

  return (
    <Spin spinning={loadingSave}>
      <Flex vertical style={{ minHeight: 450 }}>

        {isFormOpen ? null : (
          <TwoPaneCascaderSearch
            placeholder={t("contacts.groups.enter.email.WhatsApp")}
            options={cascaderOptions as any}
            open={cascaderOpen}
            onOpenChange={(open) => {
              setCascaderOpen(open);
              if (open && contacts.length === 0) getContacts();
            }}
            loading={loadingContacts}
            searchTerm={searchTerm}
            inputValue={inputValue}
            setInputValue={setInputValue}
            onSearch={handleSearch}
            onChange={onCascaderChange as any}
            notFoundContent={
              <Flex vertical align="center" gap={8}>
                <Text type="secondary">No se encontraron contactos</Text>
              </Flex>
            }
            footer={
              <Button
                size="small"
                type="link"
                icon={<PlusOutlined />}
                onClick={() => {
                  setCascaderOpen(false);
                  handleAddContact();
                }}
              >
                {t("button.createnewcontact")}
              </Button>
            }
          />
        )}


        <Form layout="vertical" style={{ marginTop: 10 }} form={form}>
          {isFormOpen && (
            <NewContactCard
              disabled={loadingSave}
              onBlurValidate={handleNewContactBlurValidate}
            />
          )}

          {isSelected && selectedContact && (
            <Flex vertical gap={4} className="card-permissions" style={{border: '1px solid #f0f0f0', borderRadius: 8, overflow: "hidden" }}>
              <Flex gap={4} style={{backgroundColor: "#f0f0f0", padding: 8,}}>
                {getRoleIcon(selectedContact.role)}
                <Text strong style={{ textTransform: "capitalize"}}>  {selectedContact.role}</Text>
              </Flex>
              <Flex justify="space-between" align="center" style={{ padding: 8 }}>
                <Text strong>{selectedContact.name}</Text>
                {selectedContact.role === "customer" && primaryPermission && (
                  <Select
                    size="small"
                    style={{ width: 120 }}
                    value={selectedValues[primaryPermission.code]}
                    onChange={(v) => setPermissionValue(primaryPermission.code, v)}
                  >
                    {selectOptions.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                  </Select>
                )}
              </Flex>

              <Flex vertical gap={12}>
                {selectedContact.role !== "customer" && (
                  <>
                    {permissionList.map((p) => (
                      <Flex
                        key={p.code}
                        align="center"
                        justify="space-between"
                      >
                        <Text>
                          {p.badge ? <Badge status="default" size="small" /> : null}
                          {p.badge ? " " : ""}
                          {p.label}
                        </Text>
                        {p.asSwitch ? (
                          <Switch
                            size="small"
                            checked={
                              selectedValues[p.code] === "admin" ||
                              selectedValues[p.code] === "editor"
                            }
                            onChange={(on) =>
                              setPermissionValue(p.code, on ? SWITCH_ON : SWITCH_OFF)
                            }
                          />
                        ) : (
                          <Select
                            size="small"
                            style={{ width: 120 }}
                            value={selectedValues[p.code]}
                            onChange={(v) => setPermissionValue(p.code, v)}
                          >
                            {selectOptions.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                          </Select>
                        )}
                      </Flex>
                    ))}
                  </>
                )}
              </Flex>
            </Flex>
          )}
        </Form>

        <Flex justify="space-between" gap={8} style={{ marginTop: "auto", paddingTop: 20 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={handleCancel}>{t("buttons.back")}</Button>
          <Button
            type="primary"
            icon={<CheckOutlined />} iconPosition="end"
            /* Sin `conflictChecking`: el blur del campo dispara antes que el click y se perdía el
               primer click (antd ignora el click de un botón en `loading`). El guardado corre el
               chequeo igual antes de crear. */
            disabled={
              (!isFormOpen && !isSelected) ||
              (!hasAnySelectedValue && !isFormOpen)
            }
            onClick={async () => {
              // Pre-submit conflict check for the new-contact form. Mirrors
              // the on-blur lookup so races (typing + Save quickly) still
              // get blocked.
              if (isFormOpen) {
                const peek = form.getFieldsValue();
                const { whatsappError, emailError } = await runConflictCheck(
                  peek.whatsapp,
                  peek.email,
                  store?.code,
                );
                if (whatsappError || emailError) {
                  applyConflictErrors(whatsappError, emailError);
                  return;
                }
              }

              setLoadingSave(true);
              try {
                if (isFormOpen) {
                  const values = await form.validateFields();
                  if (!store?.code) {
                    throw new Error("Falta storeCode");
                  }

                  const createdUser = await axiosInstance.post(
                    `${API_BASE_URL}/users`,
                    {
                      email: values.email,
                      whatsapp: values.whatsapp,
                      firstname: values.firstname,
                      lastname: values.lastname,
                      password: generateRandomPassword(20),
                      storeCode: store.code,
                    },
                  );

                  const newUserId = createdUser?.data?.user?.userId;
                  if (!newUserId) {
                    throw new Error("No se obtuvo el userId del nuevo usuario");
                  }

                  const createdContact = await axiosInstance.post(
                    `${API_BASE_URL}/contacts`,
                    {
                      storeCode: store.code,
                      userId: newUserId,
                      // OpenSpec 024 — el nombre tipeado es de la ficha de este store. Sin esto la
                      // ficha nace sin nombre propio y muestra el de la identidad, que cuando la
                      // persona ya existía en otro store es un nombre que el operador no escribió.
                      firstname: values.firstname,
                      lastname: values.lastname,
                      description: "",
                      isActive: true,
                      additionalData: {},
                    },
                  );

                  const newContactId = createdContact?.data?.contactId;
                  if (!newContactId) {
                    throw new Error("No se obtuvo el contactId del nuevo contacto");
                  }

                  // A freshly created contact has no operator memberships,
                  // so it can only be added as a Customer participant with
                  // editor access on the primary permission.
                  const newContact: ContactOption = {
                    id: newUserId,
                    contactId: newContactId,
                    name: `${values.firstname} ${values.lastname}`.trim(),
                    email: values.email ?? "",
                    whatsapp: values.whatsapp ?? "",
                    type: "contact",
                    role: "customer",
                    roleCode: "",
                  };

                  await onSaveContact(
                    newContact,
                    primaryPermission
                      ? { [primaryPermission.code]: "editor" }
                      : {},
                  );
                } else if (selectedContact) {
                  await onSaveContact(selectedContact, selectedValues);
                }
                resetFormState();
                handleCancel();
              } catch (e: any) {
                if (e?.errorFields) {
                  return;
                }
                // Backend BadRequestException with {code, field, message} for
                // identity conflicts that slipped past the on-blur lookup.
                if (consumeBackendConflict(e)) {
                  return;
                }
                console.error("Error al guardar", e);
                message.error("Error al guardar");
              } finally {
                setLoadingSave(false);
              }
            }}
          >
            {t("buttons.save")}
          </Button>
        </Flex>
      </Flex>
    </Spin>
  );
};
